import { Text } from '@/components/atoms/text';
import { Flex } from '@/styles/globalStyles';
import { CompareButton, InsightButton, StyledButtons, StyledNoComparison } from './styles';
import { BarChart4Icon } from '@/components/atoms/customIcons/barChart4';
import { CompareArrows, FileDownloadOutlined } from '@mui/icons-material';
import { SparklesIcon } from '@/components/atoms/customIcons/sparklesIcon';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from '@/hooks/useTranslation';
import { Mobile } from '../viewport/mobile';
import { Box, useMediaQuery } from '@mui/material';
import { Desktop } from '../viewport/desktop';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { MenuButton, MenuButtonOption } from '../button/menu';
import { DownloadUsage } from '@/graphql/types/downloads';

interface MunicipalityActionsProps {
  hasComparison?: boolean;
  compareButton: {
    onClick: () => void;
  };
  insightButton: {
    onClick: () => void;
  };
  downloadOptions?: MenuButtonOption[];
  loadingPDF?: boolean;
  loadingXLSX?: boolean;
  downloadUsage?: DownloadUsage;
  limitReachedMessage?: string;
}

export const MunicipalityActions = ({
  hasComparison,
  compareButton,
  insightButton,
  downloadOptions,
  loadingPDF,
  loadingXLSX,
  downloadUsage,
  limitReachedMessage,
}: MunicipalityActionsProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { t } = useTranslation();

  return (
    <Flex direction="column" gap={2} fullWidth>
      <Flex justify="space-between" align={isMobile ? 'flex-start' : 'center'} fullWidth>
        <Text variant={isMobile ? 'h6' : 'h5'} color="text.primary">
          <SafeMarkdown text={t('label.label.municipality_comparison')} />
        </Text>

        {hasComparison && (
          <Desktop>
            <Flex gap={2}>
              <InsightButton variant="outlined" onClick={() => insightButton.onClick()}>
                <SparklesIcon color="inherit" />
                <SafeMarkdown text={t('button.button.insights')} />
              </InsightButton>

              {downloadOptions && downloadOptions.length > 0 && (
                <MenuButton
                  options={downloadOptions}
                  icon={FileDownloadOutlined}
                  label={t('button.button.export')}
                  loading={loadingPDF || loadingXLSX}
                  downloadUsage={downloadUsage}
                  limitReachedMessage={limitReachedMessage}
                />
              )}
            </Flex>
          </Desktop>
        )}
      </Flex>

      {!hasComparison && (
        <StyledNoComparison>
          <Box sx={{ paddingBottom: theme.spacing(2) }}>
            <BarChart4Icon color="action" sx={{ fontSize: 80 }} />
          </Box>

          <Desktop>
            <CompareButton onClick={() => compareButton.onClick()}>
              <CompareArrows sx={{ color: theme.palette.text.primary }} />
              <SafeMarkdown text={t('button.button.compare_municipality')} />
            </CompareButton>
          </Desktop>
        </StyledNoComparison>
      )}

      <Mobile>
        <StyledButtons>
          <CompareButton onClick={() => compareButton.onClick()} fullWidth>
            <CompareArrows sx={{ color: theme.palette.text.primary }} />
            <SafeMarkdown text={t('button.button.compare_municipality')} />
          </CompareButton>

          <InsightButton variant="outlined" onClick={() => insightButton.onClick()} fullWidth>
            <SparklesIcon color="inherit" />
            <SafeMarkdown text={t('button.button.insights')} />
          </InsightButton>

          {downloadOptions && downloadOptions.length > 0 && (
            <MenuButton
              options={downloadOptions}
              icon={FileDownloadOutlined}
              label={t('button.button.export')}
              loading={loadingPDF || loadingXLSX}
              downloadUsage={downloadUsage}
              limitReachedMessage={limitReachedMessage}
            />
          )}
        </StyledButtons>
      </Mobile>
    </Flex>
  );
};
